import { Component, signal, computed, input, inject, OnInit, HostListener, ElementRef } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';

import { OrdersService } from '../orders.service';
import { Order } from '../order.model';
import { OrderItem } from '../order-item/order-item';
import { DoneOrderReminderComponent } from './done-order-reminder/done-order-reminder.component';
import { NotificationDialogComponent } from '../../notification-dialog/notification-dialog.component';
import { NotificationService } from '../../../shared/services/notification.service';
import { OrderFilterButton } from './order-filter-button/order-filter-button.component';
import { IconComponent } from '../../../shared/icon/icon.component';

export type OrderFilterKey =
  | 'waitingForApproval'
  | 'processing'
  | 'done'
  | 'rejected'
  | 'collected';

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [OrderItem, DoneOrderReminderComponent, NotificationDialogComponent, OrderFilterButton, IconComponent],
  templateUrl: './order-list.component.html',
  styleUrl: './order-list.component.css'
})
export class OrderListComponent implements OnInit {
  private readonly ordersService = inject(OrdersService);
  private readonly functions = inject(Functions);
  private readonly auth = inject(Auth);
  private readonly notificationService = inject(NotificationService);
  private readonly elementRef = inject(ElementRef);
  
  filter = signal<OrderFilterKey>('waitingForApproval');
  userId = input<string>();
  companyId = input<string>();
  orders = signal<Order[]>([]);
  isDropdownOpen = signal(false);
  showReminderDialog = signal(false);
  showNotificationDialog = signal(false);

  readonly notificationsNotAllowed = computed(() => 
    this.notificationService.notificationPermission() !== 'granted'
  );

  doneOrders = computed(() => 
    this.orders().filter(order => order.status === 'done')
  );

  currentUserId = computed(() => this.auth.currentUser?.uid ?? '');

  hasUnseenComments = computed(() => {
    const userId = this.currentUserId();
    if (!userId) return { waitingForApproval: false, processing: false, done: false };

    const allOrders = this.orders();
    
    return {
      waitingForApproval: allOrders
        .filter(order => order.status === 'waitingForApproval')
        .some(order => this.ordersService.getUnseenCommentsCount(order, userId) > 0),
      
      processing: allOrders
        .filter(order => order.status === 'processing')
        .some(order => this.ordersService.getUnseenCommentsCount(order, userId) > 0),
      
      done: allOrders
        .filter(order => order.status === 'done')
        .some(order => this.ordersService.getUnseenCommentsCount(order, userId) > 0)
    };
  });

  ngOnInit() {
    const currentUserId = this.userId();

    if (currentUserId) {
      this.ordersService.getAllUserOrders(currentUserId, this.companyId()).subscribe(orders => {
        this.orders.set(orders);
      });
    } else {
      this.ordersService.getOrders().subscribe(orders => {
        this.orders.set(orders);
      });
    }
  }
  
  filteredOrders = computed(() => {
    const allOrders = this.orders();
    const currentFilter = this.filter();
    
    if (currentFilter === 'waitingForApproval') {
      return allOrders.filter(order => order.status === 'waitingForApproval');
    }
    if (currentFilter === 'processing') {
      return allOrders.filter(order => order.status === 'processing');
    }
    if (currentFilter === 'done') {
      return allOrders.filter(order => order.status === 'done');
    }
    if (currentFilter === 'rejected') {
      return allOrders.filter(order => order.status === 'rejected');
    }
    if (currentFilter === 'collected') {
      return allOrders.filter(order => order.status === 'collected');
    }
    return allOrders.filter(order => 
      order.status !== 'waitingForApproval' && order.status !== 'done' && order.status !== 'processing'
    );
  });

  filters = computed<{ key: OrderFilterKey; label: string; count: number; hasUnseen: boolean; operatorOnly: boolean }[]>(() => {
    const all = this.orders();
    const unseen = this.hasUnseenComments();
    const countBy = (pred: (o: Order) => boolean) => all.filter(pred).length;

    return [
      { key: 'waitingForApproval', label: 'Waiting for approval',
        count: countBy(o => o.status === 'waitingForApproval'),
        hasUnseen: unseen.waitingForApproval, operatorOnly: false },
      { key: 'processing', label: 'Processing',
        count: countBy(o => o.status === 'processing'),
        hasUnseen: unseen.processing, operatorOnly: false },
      { key: 'done', label: 'Done',
        count: countBy(o => o.status === 'done'),
        hasUnseen: unseen.done, operatorOnly: false },
      { key: 'rejected', label: 'Rejected',
        count: countBy(o => o.status === 'rejected'),
        hasUnseen: false, operatorOnly: true },
      { key: 'collected', label: 'Collected',
        count: countBy(o => o.status === 'collected'),
        hasUnseen: false, operatorOnly: true },
    ];
  });

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  openReminderDialog() {
    this.isDropdownOpen.set(false);
    this.showReminderDialog.set(true);
  }

  closeReminderDialog() {
    this.showReminderDialog.set(false);
  }

  openNotificationDialog() {
    this.isDropdownOpen.set(false);
    this.showNotificationDialog.set(true);
  }

  closeNotificationDialog() {
    this.showNotificationDialog.set(false);
  }

  enableNotifications() {
    this.showNotificationDialog.set(false);
    this.notificationService.requestPermission().subscribe();
  }

  async sendReminder() {
    this.showReminderDialog.set(false);
    try {
      const remindUsers = httpsCallable(this.functions, 'remindUsersWithDoneOrders');
      await remindUsers();
      alert('Reminder sent successfully!');
    } catch (error) {
      console.error('Error calling function:', error);
      alert('Failed to send reminder. Please try again.');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement
      .querySelector('.dropdown-container')
      ?.contains(event.target as Node);
    
    if (!clickedInside && this.isDropdownOpen()) {
      this.isDropdownOpen.set(false);
    }
  }
}
